/**
 * The light preview's lifecycle (light-preview plan F2).
 *
 * The file store is the source of truth for a prototype's code, and the editor, the AI's write
 * tools and click-to-edit all write there already — so the preview just follows the store: on
 * every change it recompiles in the tab and points the iframe at a fresh document. Nothing is
 * written to a filesystem, no dev server runs, and no npm install happens.
 *
 * Kept free of React and of `fetch` so it can be driven in tests.
 */

import type { PreviewCompileResult, PreviewCompiler } from './compiler';
import {
  buildPreviewDocument,
  createPreviewDocumentUrl,
  type PreviewDocumentInput,
} from './previewDocument';
import { forgetPreviewDocument, rememberPreviewDocument } from './previewSourceFile';
import type { PreviewFiles } from './moduleGraph';

/** Enough to coalesce a burst of writes (a turn writing 15 files) into one compile. */
export const COMPILE_DEBOUNCE_MS = 150;

export interface LightPreviewPublisher {
  /** The iframe's URL; the previous one is revoked once the new document is live. */
  setUrl: (url: string) => void;
  setErrors: (errors: string[]) => void;
  setUnsupportedPackages: (packages: string[]) => void;
  /** True while a compile is in flight, so the workbench can show progress. */
  setCompiling: (compiling: boolean) => void;
}

export interface LightPreviewOptions {
  compiler: PreviewCompiler;
  publisher: LightPreviewPublisher;
  /** Current project files; read fresh on every compile. */
  getFiles: () => PreviewFiles;
  /** The workspace's binary assets; images the generated code references. */
  getAssets?: () => Readonly<Record<string, { base64: string; mimeType?: string }>>;
  /**
   * True while a turn is still writing files. Mid-turn the workspace is half-written — `App.tsx`
   * lands before the pages it imports — so compile errors are expected and gone by the next
   * write. They are not reported while this is true; the compile that runs when the turn ends
   * reports whatever is genuinely broken.
   */
  isMidTurn?: () => boolean;
  /** Calls back on every file change; returns an unsubscribe. */
  subscribe: (onChange: () => void) => () => void;
  /** Fixed parts of the document (import map, Tailwind, bridge). */
  document: Omit<PreviewDocumentInput, 'appJs' | 'css'>;
  debounceMs?: number;
  /** Swappable in tests, where `URL.createObjectURL` does not exist. */
  createUrl?: (html: string) => string;
  revokeUrl?: (url: string) => void;
  onCompiled?: (result: PreviewCompileResult) => void;
}

export interface LightPreview {
  /** Compile now, without waiting for a change (first render, or "Reload preview"). */
  refresh: () => Promise<void>;
  stop: () => void;
}

export function startLightPreview(options: LightPreviewOptions): LightPreview {
  const {
    compiler,
    publisher,
    getFiles,
    getAssets,
    isMidTurn,
    subscribe,
    document: documentParts,
    debounceMs = COMPILE_DEBOUNCE_MS,
    createUrl = createPreviewDocumentUrl,
    revokeUrl = (url: string) => URL.revokeObjectURL(url),
    onCompiled,
  } = options;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let running: Promise<void> | null = null;
  /** A change during a compile: recompile once it finishes, rather than queueing one per change. */
  let changedWhileCompiling = false;
  let currentUrl: string | null = null;

  const compileNow = async (): Promise<void> => {
    if (stopped) return;
    publisher.setCompiling(true);
    try {
      const result = await compiler.compile(getFiles(), getAssets?.());
      if (stopped) return;

      publisher.setErrors(isMidTurn?.() ? [] : result.errors);
      publisher.setUnsupportedPackages(result.unsupported);
      onCompiled?.(result);

      // A failed compile keeps the last working document on screen; the overlay reports why.
      if (result.js) {
        const html = buildPreviewDocument({
          ...documentParts,
          appJs: result.js,
          css: result.css,
          bridgeJs: result.signalJs ?? documentParts.bridgeJs,
        });
        const next = createUrl(html);
        // Kept so a crash's `blob:…:line` frame can be traced to its file (previewSourceFile.ts).
        rememberPreviewDocument(next, html);
        const previous = currentUrl;
        currentUrl = next;
        publisher.setUrl(next);
        if (previous) {
          revokeUrl(previous);
          forgetPreviewDocument(previous);
        }
      }
    } catch (err) {
      if (!stopped && !isMidTurn?.()) {
        publisher.setErrors([err instanceof Error ? err.message : String(err)]);
      }
    } finally {
      if (!stopped) publisher.setCompiling(false);
    }

    if (changedWhileCompiling && !stopped) {
      changedWhileCompiling = false;
      await compileNow();
    }
  };

  const run = (): Promise<void> => {
    if (running) {
      changedWhileCompiling = true;
      return running;
    }
    running = compileNow().finally(() => {
      running = null;
    });
    return running;
  };

  const unsubscribe = subscribe(() => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, debounceMs);
  });

  return {
    refresh: () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      return run();
    },
    stop: () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      unsubscribe();
      if (currentUrl) {
        revokeUrl(currentUrl);
        currentUrl = null;
      }
    },
  };
}
